import type { ReactNode } from "react";
import { theme } from "../theme.ts";
import type { CoverSubtype } from "@march/shared";

/** Segmented-control style pill button. */
export function FilterPill<T extends string>({
  value,
  active,
  onClick,
  children,
}: {
  value: T;
  active: boolean;
  onClick: (v: T) => void;
  children: ReactNode;
}) {
  return (
    <button type="button" className={`tab fpill${active ? " on" : ""}`} aria-pressed={active} onClick={() => onClick(value)}>
      {children}
    </button>
  );
}

/** Secondary button — outline / low-emphasis. */
export function OutlineButton({
  onClick,
  disabled,
  children,
  style,
}: {
  onClick: () => void;
  disabled?: boolean;
  children: ReactNode;
  style?: React.CSSProperties;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: `${theme.space.sm}px ${theme.space.md}px`,
        borderRadius: theme.radius.md,
        border: `1px solid ${theme.color.border}`,
        background: theme.color.surface,
        color: disabled ? theme.color.textFaint : theme.color.text,
        cursor: disabled ? "not-allowed" : "pointer",
        fontSize: theme.font.size.md,
        fontWeight: theme.font.weight.medium,
        transition: "all 100ms ease",
        ...style,
      }}
      onMouseEnter={(e) => {
        if (!disabled)
          e.currentTarget.style.borderColor = theme.color.borderStrong;
      }}
      onMouseLeave={(e) => {
        if (!disabled) e.currentTarget.style.borderColor = theme.color.border;
      }}
    >
      {children}
    </button>
  );
}

/**
 * Cover-subtype secondary filter — shown as a smaller pill strip
 * beneath the primary type row when Cover is active. "All" preserves
 * the current behaviour (Cover + CoverBase combined).
 */
export type CoverSubtypeFilter = "All" | CoverSubtype;
const COVER_SUBTYPES: CoverSubtypeFilter[] = [
  "All",
  "Olefin",
  "Pacifica",
  "Sunbrella",
];

export function CoverSubtypeFilterRow({
  value,
  onChange,
  counts,
}: {
  value: CoverSubtypeFilter;
  onChange: (v: CoverSubtypeFilter) => void;
  counts?: Partial<Record<CoverSubtypeFilter, number>>;
}) {
  return (
    <div
      style={{
        display: "flex",
        gap: theme.space.xs,
        alignItems: "center",
        flexWrap: "wrap",
        paddingLeft: theme.space.xs,
        borderLeft: `3px solid ${theme.color.brandFaint}`,
      }}
    >
      <span
        style={{
          fontSize: theme.font.size.xs,
          color: theme.color.textMuted,
          textTransform: "uppercase",
          letterSpacing: 0.4,
          marginRight: theme.space.xs,
          fontWeight: theme.font.weight.medium,
        }}
      >
        Cover type
      </span>
      {COVER_SUBTYPES.filter(
        // "All" always shows; a subtype pill shows unless counts were
        // provided and it's empty (e.g. Sunbrella in Monitor, which has no
        // CoverBase rows). Avoids dead pills that filter to nothing.
        (st) => st === "All" || !counts || (counts[st] ?? 0) > 0,
      ).map((st) => {
        const active = value === st;
        const count = counts?.[st];
        return (
          <button
            key={st}
            type="button"
            onClick={() => onChange(st)}
            style={{
              padding: `4px 10px`,
              borderRadius: theme.radius.sm,
              border: `1px solid ${active ? theme.color.brand : theme.color.border}`,
              background: active ? theme.color.brandFaint : theme.color.surface,
              color: active ? theme.color.brand : theme.color.textMuted,
              cursor: active ? "default" : "pointer",
              fontSize: theme.font.size.sm,
              fontWeight: active
                ? theme.font.weight.semibold
                : theme.font.weight.regular,
              transition: "all 100ms ease",
            }}
          >
            {st}
            {typeof count === "number" && (
              <span
                style={{
                  marginLeft: 6,
                  fontSize: theme.font.size.xs,
                  color: theme.color.textFaint,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/** Primary CTA. */
export function PrimaryButton({
  onClick,
  disabled,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: `${theme.space.sm}px ${theme.space.md}px`,
        borderRadius: theme.radius.md,
        border: 0,
        background: disabled ? theme.color.borderStrong : theme.color.brand,
        color: theme.color.onBrand,
        cursor: disabled ? "not-allowed" : "pointer",
        fontSize: theme.font.size.md,
        fontWeight: theme.font.weight.semibold,
        transition: "background 100ms ease",
        boxShadow: theme.shadow.sm,
      }}
      onMouseEnter={(e) => {
        if (!disabled)
          e.currentTarget.style.background = theme.color.brandHover;
      }}
      onMouseLeave={(e) => {
        if (!disabled) e.currentTarget.style.background = theme.color.brand;
      }}
    >
      {children}
    </button>
  );
}
